import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { useSports, useThemesRef } from '@/features/reference/useReference';
import type { ScalingSetting, ScalingCreatePayload } from '@/api/scalingService';
import { DEFAULT_ROUNDING_STEP, ROUNDING_OPTIONS } from './rounding';
import { useScalableFormats } from './formatOptions';

interface ScalingModalProps {
  open: boolean;
  onClose: () => void;
  setting: ScalingSetting | null;
  onCreate: (payload: ScalingCreatePayload) => Promise<unknown>;
  onUpdate: (
    payload: { formatId: string; themeId: string; sportIdentifier: string; factor: number; roundingStep: number }
  ) => Promise<unknown>;
  isSaving: boolean;
}

const ScalingModal = ({ open, onClose, setting, onCreate, onUpdate, isSaving }: ScalingModalProps) => {
  const { data: themes = [] } = useThemesRef();
  const { data: sports = [] } = useSports();
  const formats = useScalableFormats();
  const isEdit = Boolean(setting);

  const [themeId, setThemeId] = useState('');
  const [sportIdentifier, setSportIdentifier] = useState('');
  const [formatId, setFormatId] = useState('');
  const [factor, setFactor] = useState('1.00');
  const [roundingStep, setRoundingStep] = useState(DEFAULT_ROUNDING_STEP);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setThemeId(setting?.themeId ?? '');
      setSportIdentifier(setting?.sportIdentifier ?? '');
      setFormatId(setting?.formatId ?? '');
      setFactor(setting ? setting.factor.toFixed(2) : '1.00');
      setRoundingStep(setting?.roundingStep ?? DEFAULT_ROUNDING_STEP);
    }
  }, [open, setting]);

  const themeLabel = setting?.themeLabel ?? themes.find((t) => t.id === themeId)?.label ?? '';
  const sportLabel = sports.find((s) => s.identifier === sportIdentifier)?.label ?? sportIdentifier;
  const formatLabel =
    formats.find((f) => f.id === (setting?.formatId ?? formatId))?.optionLabel ?? setting?.formatLabel ?? '';

  const factorValue = Number(factor);
  const softWarn = useMemo(
    () => Number.isFinite(factorValue) && factorValue > 0 && (factorValue < 0.5 || factorValue > 2),
    [factorValue]
  );

  const handleSubmit = async () => {
    setError('');
    if (!isEdit && (!themeId || !sportIdentifier || !formatId)) {
      return setError('Thème, sport et format sont requis.');
    }
    if (!Number.isFinite(factorValue) || factorValue <= 0) {
      return setError('Le facteur doit être un nombre supérieur à 0.');
    }
    try {
      if (isEdit && setting) {
        await onUpdate({
          formatId: setting.formatId,
          themeId: setting.themeId,
          sportIdentifier: setting.sportIdentifier,
          factor: factorValue,
          roundingStep,
        });
      } else {
        await onCreate({ formatId, themeId, sportIdentifier, factor: factorValue, roundingStep });
      }
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? 'Modifier le réglage' : 'Ajouter un réglage de scaling'}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {isEdit ? (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-500">Thème</span>
              <p className="text-gray-900 dark:text-gray-100">{themeLabel}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Sport</span>
              <p className="text-gray-900 dark:text-gray-100">{sportLabel}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Format</span>
              <p className="text-gray-900 dark:text-gray-100">{formatLabel}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <Select label="Thème" value={themeId} onChange={(e) => setThemeId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
            <Select label="Sport" value={sportIdentifier} onChange={(e) => setSportIdentifier(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {sports.map((s) => (
                <option key={s.identifier} value={s.identifier}>
                  {s.label}
                </option>
              ))}
            </Select>
            <Select label="Format" value={formatId} onChange={(e) => setFormatId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {formats.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.optionLabel}
                </option>
              ))}
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Facteur (×)"
            type="number"
            step="0.05"
            min="0.1"
            max="5"
            value={factor}
            onChange={(e) => setFactor(e.target.value)}
          />
          <Select
            label="Arrondi"
            value={String(roundingStep)}
            onChange={(e) => setRoundingStep(Number(e.target.value))}
          >
            {ROUNDING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        {softWarn && (
          <p className="text-xs text-yellow-600">
            Facteur inhabituel (hors 0,5–2,0) — vérifiez qu’il ne s’agit pas d’une faute de frappe.
          </p>
        )}

        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

export default ScalingModal;
